import { DimensionPickerDialogWithProvider } from '@amzn/gft-dimension-picker';
import { DimSumEnhancedNode } from '@amzn/gft-dimsum-service';
import { KPI_DIMENSION_CONFIG_MAP, KPI_DIMENSION_RENDER_MAP } from '@constants/page';
import { useDeckContext } from '@context/DeckContext';
import { usePageContext } from '@context/PageContext';
import {
  buildCalcMembers,
  calcMembersToDisplayTokens,
  calcMembersToSegments,
  columnOwnedDimensions,
} from '@deckManagement/components/Common/CalculatedKPIBuilder/hooks/useCalcMembers';
import SingleDimensionDialog from '@deckManagement/components/Common/CalculatedKPIBuilder/SingleDimensionDialog';
import {
  formulaToKpiSegments,
  generateFormula,
  isSupportedFormula,
  splitRawFormula,
} from '@deckManagement/components/Common/CalculatedKPIBuilder/utils/formulaHelpers';
import { KpiFormulaNode } from '@deckManagement/types/CalculatedKPIBuilder.types';
import EditIcon from '@mui/icons-material/Edit';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { DatasetSource } from '@services/api/deckManagement/graphql/schema/API';
import { isEmpty, isNil, isNull } from 'lodash';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { PartialKPIDrivers } from 'types/Page.types';
import { CalculatedKPIPanelProps, KpiDimension } from 'types/PageSidePanel.types';

const CalculatedKPIPanel = (props: CalculatedKPIPanelProps) => {
  const {
    initialFormula,
    initialKpiId,
    pageDrivers,
    onFormulaChange,
    onCalcMembersChange,
    calcMembers,
    pageLayout,
    alwaysInclude,
    disabled = false,
  } = props;
  const datasetSource = props.datasetSource ?? DatasetSource.GALAXY;
  const [rawFormula, setRawFormula] = useState<string | undefined>('');
  const [localFormulas, setLocalFormulas] = useState<ReturnType<typeof splitRawFormula>>({});
  const [isSupportedCalculatedKpi, setIsSupportedCalculatedKpi] = useState<boolean>(false);
  const [formulaDialogOpen, setFormulaDialogOpen] = useState<boolean>(false);
  const [formulaEditKey, setFormulaEditKey] = useState<string | null>(null);
  const [dimsumDialogOpen, setDimsumDialogOpen] = useState<boolean>(false);
  const [dimsumEditKey, setDimsumEditKey] = useState<string | null>(null);
  // Resolved nodes that back the authored expression (keyed by the expression dimension) and each
  // pinned dimension. These carry the DimSum hierarchy (`attributes.id`) so `calc_members` can be
  // sent complete — the authoring path never re-resolves a node (handoff §3/§6c).
  const [expressionSegments, setExpressionSegments] = useState<KpiFormulaNode[]>([]);
  const [pinnedNodes, setPinnedNodes] = useState<Record<string, DimSumEnhancedNode>>({});
  const { modifiedPageProperties } = usePageContext();
  const { page_drivers: currentPageKPIDrivers } = modifiedPageProperties || {};
  const { rootNodes } = useDeckContext();

  const ticketTemplateUrl = 'https://t.corp.amazon.com/create/templates/90236c33-68ef-4c65-992a-2159b393c0fc';

  /**
   * Whether a served `calc_members` is present (the row decodes to a single-dimension expression).
   * A served row is read-only — editing it would need the node hierarchies the served tokens omit
   * (handoff §4/§6c). New/authored rows have no served members and take the editable path.
   */
  const hasServedCalcMembers = !isNil(calcMembers);

  /** The ordered display tokens for a served expression, or null when none. */
  const displayTokens = useMemo(() => calcMembersToDisplayTokens(calcMembers), [calcMembers]);

  /**
   * Seed `expressionSegments` from the served `calc_members` so a pin-only edit preserves the authored
   * expression. `handleDimensionEditConfirm` re-emits `emitCalcMembers(expressionSegments, …)`; without
   * this seed those segments are `[]` for a served row (only `handleEditConfirm` populates them), so a
   * pin edit would serialize empty `tokens` and drop the expression.
   *
   * Only seeds while `expressionSegments` is empty, so it never clobbers segments authored this
   * session. Cross-row staleness (reusing one panel instance across deckline selections) is handled by
   * keying the panel on the deckline in `PageSidePanel`, so a row switch REMOUNTS this component and
   * all per-row state (`expressionSegments`, `pinnedNodes`, …) starts fresh.
   */
  useEffect(() => {
    if (isNil(calcMembers)) {
      return;
    }
    setExpressionSegments((prev) => (prev.length === 0 ? calcMembersToSegments(calcMembers) : prev));
  }, [calcMembers]);

  /** Dimensions the page's columns own — never offered as a pin (handoff §6b). */
  const columnOwned = useMemo(() => columnOwnedDimensions(pageLayout), [pageLayout]);

  /**
   * The expression dimension — the single `isFormula` entry in the parsed per-dimension state, else
   * the served `calc_members.dimension`.
   */
  const expressionDimensionKey = useMemo<string | null>(() => {
    const fromLocal = Object.entries(localFormulas).find(([, entry]) => entry.isFormula)?.[0];
    return fromLocal ?? calcMembers?.dimension ?? null;
  }, [localFormulas, calcMembers]);

  /**
   * Emits the authored expression as `calc_members` (design §4.2). The expression dimension's segments
   * become tokens (hierarchy from each node), the pinned dimensions become pins (hierarchy from the
   * picked node); column-owned and the expression dimension itself are excluded.
   */
  const emitCalcMembers = useCallback(
    (segments: KpiFormulaNode[], pins: Record<string, DimSumEnhancedNode>, dimension: string | null) => {
      if (!onCalcMembersChange || isNil(dimension) || dimension === '') {
        return;
      }
      const members = buildCalcMembers({
        expressionDimension: dimension,
        segments,
        pinnedNodes: pins,
        columnOwnedDimensions: columnOwned as Set<string>,
        datasetSource,
      });
      if (members) {
        onCalcMembersChange(members);
      }
    },
    [columnOwned, datasetSource, onCalcMembersChange],
  );

  /**
   * Generate non-formula elements for pageDrivers which are not root nodes.
   */
  const nonRootPageDrivers = useCallback(() => {
    const result: ReturnType<typeof splitRawFormula> = {};

    Object.entries(pageDrivers).forEach(([key, driver]) => {
      if (driver?.hierarchy && driver?.value) {
        const nodeId = `${driver.hierarchy}-${driver.value}`;
        const isRootNode = rootNodes?.[key]?.some((node) => node?.nodeID === nodeId);

        if (!isRootNode) {
          result[key] = {
            value: driver.value,
            isFormula: false,
          };
        }
      }
    });

    return result;
  }, [pageDrivers, rootNodes]);

  /**
   * Sets the raw formula when initialFormula changes
   */
  useEffect(() => {
    setRawFormula((prev) => {
      if (prev !== initialFormula) {
        // reset state when raw formula changes
        setLocalFormulas({});
        setFormulaEditKey(null);
        return initialFormula;
      }

      return prev;
    });
  }, [initialFormula]);

  /**
   * Parses the raw formula and sets the localFormulas
   */
  useEffect(() => {
    // A new/empty calculated KPI: no served expression, no baked formula, and no id beyond the legacy
    // `|Calc|` sentinel. The main calc flow no longer seeds that sentinel (handoff §5.5), but the
    // denominator flow still passes it, so treat an empty id OR the bare sentinel as "new".
    const isSentinelOrEmptyId = isEmpty(initialKpiId) || initialKpiId === '|Calc|';
    const isNewCalculatedKpi = !hasServedCalcMembers && isSentinelOrEmptyId && isEmpty(rawFormula);

    if (rawFormula && !isEmpty(rawFormula)) {
      setLocalFormulas(() => {
        const splitFormula = {
          ...nonRootPageDrivers(),
          ...splitRawFormula(rawFormula, currentPageKPIDrivers as PartialKPIDrivers),
        };
        setIsSupportedCalculatedKpi(isSupportedFormula(rawFormula!));
        return splitFormula;
      });
    } else if (isNewCalculatedKpi) {
      // Initialize empty state for new calculated KPI
      setLocalFormulas({ ...nonRootPageDrivers() });
      setIsSupportedCalculatedKpi(true);
    } else {
      setIsSupportedCalculatedKpi(false);
    }
  }, [rawFormula, initialKpiId, hasServedCalcMembers, nonRootPageDrivers, currentPageKPIDrivers]);

  /**
   * Handles the start of formula editing by tracking the Edit Key
   */
  const handleFormulaEditStart = useCallback((key: string) => {
    setFormulaDialogOpen(true);
    setFormulaEditKey(key);
  }, []);

  /**
   * Handles the start of formula editing by tracking the Edit Key
   */
  const handleDimensionEditStart = useCallback((key: string) => {
    setDimsumEditKey(key);
    setDimsumDialogOpen(true);
  }, []);

  /**
   * Updates local formula state and triggers formula regeneration.
   * @param key - The formula key to update
   * @param newValue - Partial update containing value and/or isFormula flag
   */
  const updateLocalFormulas = useCallback(
    (key: string, newValue: Partial<ReturnType<typeof splitRawFormula>[string]>) => {
      setLocalFormulas((prev) => {
        const newLocalFormulas = {
          ...prev,
          [key]: {
            ...prev[key],
            ...newValue,
          },
        };

        // Legacy id/formula emit (kept for the denominator flow + any caller still using it).
        const updatedFormulaData = generateFormula(newLocalFormulas, rootNodes);
        onFormulaChange?.(updatedFormulaData.id, updatedFormulaData.formula);
        return newLocalFormulas;
      });
    },
    [onFormulaChange, rootNodes],
  );

  /**
   * Updates the local formula state when formula editing is confirmed. Captures the builder's resolved
   * segments so the expression tokens carry their DimSum hierarchy, then re-emits `calc_members`.
   */
  const handleEditConfirm = useCallback(
    (newFormula: string, segments?: KpiFormulaNode[]) => {
      if (formulaEditKey) {
        const nextSegments = segments ?? formulaToKpiSegments(newFormula);
        setExpressionSegments(nextSegments);
        updateLocalFormulas(formulaEditKey, { value: newFormula, isFormula: true });
        emitCalcMembers(nextSegments, pinnedNodes, formulaEditKey);
        setFormulaDialogOpen(false);
      }
    },
    [emitCalcMembers, formulaEditKey, pinnedNodes, updateLocalFormulas],
  );

  /**
   * Handle when a user confirms the selection of a Dimension Picker value from the DimensionPickerDialog.
   * Keeps the picked node (not just its name) so the pin carries its hierarchy, then re-emits
   * `calc_members`.
   */
  const handleDimensionEditConfirm = useCallback(
    (selectedNodes: DimSumEnhancedNode[]) => {
      if (isNull(dimsumEditKey)) {
        return;
      }

      // DimensionPicker is Single-select only.
      const selectedNode = selectedNodes[0];
      const nextPinnedNodes = { ...pinnedNodes, [dimsumEditKey]: selectedNode };
      setPinnedNodes(nextPinnedNodes);
      updateLocalFormulas(dimsumEditKey, { value: selectedNode.attributes.name, isFormula: false });
      emitCalcMembers(expressionSegments, nextPinnedNodes, expressionDimensionKey);
    },
    [dimsumEditKey, emitCalcMembers, expressionDimensionKey, expressionSegments, pinnedNodes, updateLocalFormulas],
  );

  /**
   * Render alert component for unsupported KPIs.
   */
  const renderUnsupportedAlert = useCallback(() => {
    return (
      <Alert severity='warning'>
        <Typography variant='body2'>
          To modify this Calculated KPI, please{' '}
          <Link href={ticketTemplateUrl} target='_blank'>
            Submit a Ticket
          </Link>
        </Typography>
      </Alert>
    );
  }, []);

  /**
   * Renders the raw formula (for formulas not supported by the Calculated KPI Builder)
   */
  const renderRawFormula = useCallback(() => {
    // Split the formula and insert <wbr/> elements after math operators
    // Note: <wbr> tag helps provide suggested points for word-wrapping.
    const formulaValue = (!isEmpty(initialKpiId) ? initialKpiId : rawFormula) || '';
    const parts = formulaValue.split(/([+\-*/()|])/);
    const formattedFormula = parts.map((part, index) => {
      if (/[+\-*/()|]/.test(part)) {
        return (
          <Fragment key={`formattedFormula-${index}`}>
            <wbr />
            {part}
          </Fragment>
        );
      }
      return part;
    });

    return (
      <List className='page-side-panel-calculated-kpi segments'>
        {renderUnsupportedAlert()}
        <ListItem disablePadding>
          <ListItemText
            primary={!isEmpty(initialKpiId) ? 'KPI ID' : 'KPI Formula'}
            secondary={formattedFormula}
            slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'body1' } }}
          />
        </ListItem>
      </List>
    );
  }, [initialKpiId, rawFormula, renderUnsupportedAlert]);

  /**
   * Renders the deckline segments
   */
  const renderKpiSegments = useCallback(() => {
    // Detect if we need to derive the formula dimension: when all explicit dimensions are non-formulas,
    // the missing dimension should be treated as the formula dimension
    const explicitFormulaDimensions = Object.values(localFormulas).filter((entry) => entry.isFormula).length;
    const hasImplicitFormula =
      explicitFormulaDimensions === 0 && (Object.keys(localFormulas).length > 0 || isSupportedCalculatedKpi);

    const renderList = KPI_DIMENSION_RENDER_MAP[datasetSource].filter((dimensionConfig) => {
      const pageDriver = pageDrivers[dimensionConfig.key];
      const formulaKey = dimensionConfig.decklineDrivers?.valueAccessor;

      if (isNil(formulaKey)) {
        return false;
      }

      // Column-owned dimensions are supplied per-column by the page's column overrides (handoff §6b):
      // never show them in the panel (and never offer to pin them), even though `page_drivers` leaves
      // them null. The expression dimension itself is never column-owned on a valid page.
      if (columnOwned.has(dimensionConfig.key)) {
        return false;
      }

      const formulaEntry = localFormulas[formulaKey];
      const decklineValue = formulaEntry?.value || '';

      // Display if: always included, OR pageDriver has no value (null in page_drivers), OR deckline has non-matching value
      return (
        alwaysInclude?.includes(formulaKey) ||
        !pageDriver?.value ||
        (!isEmpty(decklineValue) && pageDriver.value !== decklineValue)
      );
    });

    // Multiple calculated dimensions is currently not supported.
    const isUnsupported = explicitFormulaDimensions > 1;

    const listItems = renderList
      .map((dimensionConfig, index) => {
        const pageDriver = pageDrivers[dimensionConfig.key];
        const formulaKey = dimensionConfig.decklineDrivers?.valueAccessor;

        if (isNil(formulaKey)) {
          return null;
        }

        const formulaEntry = localFormulas[formulaKey];
        // Treat dimension as formula if explicitly marked OR if it's missing when all others are non-formulas
        const isFormula = formulaEntry?.isFormula || (hasImplicitFormula && !formulaEntry);
        const decklineValue = formulaEntry?.value || '';

        // Do not display segments which are blank or match pageDriver values
        if (
          !alwaysInclude?.includes(formulaKey) &&
          pageDriver?.value &&
          (isEmpty(decklineValue) || pageDriver.value === decklineValue)
        ) {
          return null;
        }

        return (
          <ListItem key={index} disablePadding>
            <ListItemText
              primary={dimensionConfig.label}
              secondary={decklineValue || ''}
              slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'body1' } }}
            />
            {!isUnsupported && isFormula && (
              <IconButton size='small' onClick={() => handleFormulaEditStart(formulaKey)} disabled={disabled}>
                <EditIcon fontSize='small' />
              </IconButton>
            )}
            {!isUnsupported && !isFormula && (
              <IconButton
                size='small'
                onClick={() => handleDimensionEditStart(formulaKey)}
                disabled={disabled || isUnsupported}
              >
                <EditIcon fontSize='small' />
              </IconButton>
            )}
          </ListItem>
        );
      })
      .filter((item) => !isNil(item));

    return (
      <List className='page-side-panel-calculated-kpi segments'>
        {isUnsupported && renderUnsupportedAlert()}
        {listItems}
      </List>
    );
  }, [
    alwaysInclude,
    columnOwned,
    datasetSource,
    disabled,
    handleDimensionEditStart,
    handleFormulaEditStart,
    isSupportedCalculatedKpi,
    localFormulas,
    pageDrivers,
    renderUnsupportedAlert,
  ]);

  /**
   * Handles rendering the Single Dimension Dialog
   */
  const renderSingleDimensionDialog = useCallback(() => {
    if (isNil(formulaEditKey)) {
      return null;
    }

    return (
      <SingleDimensionDialog
        open={formulaDialogOpen}
        onClose={() => setFormulaDialogOpen(false)}
        dimensionName={formulaEditKey}
        dimensionLabel={formulaEditKey}
        formula={localFormulas[formulaEditKey]?.value || ''}
        onFormulaChange={handleEditConfirm}
      />
    );
  }, [formulaDialogOpen, formulaEditKey, handleEditConfirm, localFormulas]);

  /**
   * Handles rendering of the dimension picker
   */
  const renderDimensionPicker = useCallback(() => {
    if (isNil(dimsumEditKey)) {
      return null;
    }

    return (
      <DimensionPickerDialogWithProvider
        key={`${dimsumEditKey}`}
        open={dimsumDialogOpen && !!dimsumEditKey}
        dimension={dimsumEditKey!}
        title='Dimension Hierarchy'
        onClose={() => {
          setDimsumDialogOpen(false);
          setDimsumEditKey(null);
        }}
        onConfirm={handleDimensionEditConfirm}
      />
    );
  }, [dimsumDialogOpen, dimsumEditKey, handleDimensionEditConfirm]);

  /**
   * Renders a served expression read-only, labelled by its dimension, tokens in order (R1.1, R1.4).
   * A served row is not editable in the builder (its tokens carry no hierarchy), so it is shown as a
   * single labelled expression rather than the editable per-dimension matrix.
   */
  const renderServedExpression = useCallback(() => {
    const expression = (displayTokens ?? []).map((token) => token.display).join(' ');
    const dimensionLabel = expressionDimensionKey
      ? (KPI_DIMENSION_CONFIG_MAP[expressionDimensionKey as KpiDimension]?.label ?? expressionDimensionKey)
      : 'Expression';
    return (
      <List className='page-side-panel-calculated-kpi segments'>
        <ListItem disablePadding>
          <ListItemText
            primary={dimensionLabel}
            secondary={expression}
            slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'body1' } }}
          />
        </ListItem>
      </List>
    );
  }, [displayTokens, expressionDimensionKey]);

  if (Object.values(localFormulas).length === 0 && !isSupportedCalculatedKpi && !hasServedCalcMembers) {
    return null;
  }

  // A SUPPORTED single-dimension expression — whether brand new or an existing served row — uses the
  // editable per-dimension matrix (`renderKpiSegments`), which carries the Edit affordances. The
  // read-only served display is only the fallback for a served row that is NOT builder-supported
  // (multi-dimension / opaque stem), and the legacy raw display is for an unsupported, unserved row.
  const showEditableSegments = isSupportedCalculatedKpi;
  const showServedReadOnly = hasServedCalcMembers && !isSupportedCalculatedKpi;

  return (
    <Box>
      {showEditableSegments && renderKpiSegments()}
      {!showEditableSegments && showServedReadOnly && renderServedExpression()}
      {!showEditableSegments && !showServedReadOnly && renderRawFormula()}
      {renderSingleDimensionDialog()}
      {renderDimensionPicker()}
    </Box>
  );
};

export default CalculatedKPIPanel;
